import {isAxiosError} from 'axios'
import {useEffect, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {PorQue} from '@/components/ui/por-que'
import {AuthShell} from '@/components/auth-shell'
import {HelpIcon} from '@/components/help-icon'
import {Button} from '@/components/ui/button'
import {Hint} from '@/components/ui/hint'
import {Skeleton} from '@/components/ui/skeleton'
import {Tag} from '@/components/ui/tag'
import {UploadTrigger} from '@/components/upload-trigger'
import {uploadToStorage} from '@/hooks/sgsst/evidencias/use-evidence'
import {api} from '@/lib/api'
import {translateApiError} from '@/lib/errors'
import {track} from '@/lib/telemetry'
import type {components} from '@/lib/api-schema'

type Enlace = components['schemas']['EnlaceRead']
type UploadTarget = components['schemas']['UploadTargetRead']

const BASE = '/publico/carga-ips'
// La cabecera en la que el token viaja. Nunca en la ruta: el path sí queda escrito en
// los registros de acceso de la plataforma, y no se pueden apagar.
const CABECERA_TOKEN = 'X-Carga-Token'
// La cookie de sesión es `HttpOnly`, así que el navegador la manda sola — pero solo si
// la petición la pide, y la API vive en otro origen que el sitio.
const CON_COOKIE = {withCredentials: true} as const

// Más estrecho que el interno (E1 §7): superficie anónima, y .xlsx/.docx traen zip-bomb y XML.
// El servidor exige la misma lista y el mismo tope.
const TIPOS = ['application/pdf', 'image/jpeg', 'image/png']
// Algunos móviles entregan `File.type` vacío o `image/jpg` para archivos válidos: se deduce de la
// extensión, y el juez real es el sniff de bytes del servidor (415).
const EXT_A_TIPO: Record<string, string> = {
  pdf: 'application/pdf',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
}
const ACEPTA = '.pdf,.jpg,.jpeg,.png'
const MAX_BYTES = 10 * 1024 * 1024

/** Solo un 404/410 amerita «pida un enlace nuevo»; ante otro error, renovar mataría un enlace que sirve. */
function esDelEnlace(e: unknown): boolean {
  return isAxiosError(e) && [404, 410].includes(e.response?.status ?? 0)
}

/** Única pantalla sin sesión (E1-H04): el token viaja en el fragmento y se borra tras canjearlo, y
 * no nombra a nadie porque el correo se puede reenviar dentro de la IPS. */
export function CargaIpsPublicaPage() {
  const {t} = useTranslation()
  const [enlace, setEnlace] = useState<Enlace | null>(null)
  const [error, setError] = useState<{texto: string; delEnlace: boolean} | null>(null)
  const [progress, setProgress] = useState<number | null>(null)
  const [abriendo, setAbriendo] = useState(true)
  const abierto = useRef(false)
  // Leído UNA vez, antes de que nada lo borre de la barra.
  const [token] = useState(() => window.location.hash.replace(/^#/, ''))

  /** Único camino de reintento: el token ya está en memoria y recargar perdería el `#token`. */
  function abrir() {
    setError(null)
    setAbriendo(true)
    api
      .post<Enlace>(`${BASE}/sesion`, {}, {...CON_COOKIE, headers: {[CABECERA_TOKEN]: token}})
      .then((resp) => {
        setEnlace(resp.data)
        // Se borra de la barra solo cuando el canje SALIÓ: si falla, el fragmento sigue
        // ahí y una recarga a mano —el reflejo de cualquiera— vuelve a funcionar.
        window.history.replaceState(null, '', window.location.pathname)
      })
      .catch((e: unknown) => setError({texto: translateApiError(e), delEnlace: esDelEnlace(e)}))
      .finally(() => setAbriendo(false))
  }

  useEffect(() => {
    if (abierto.current) return
    abierto.current = true
    if (!token) {
      setError({texto: t('cargaIps.errors.sinToken'), delEnlace: true})
      setAbriendo(false)
      return
    }
    abrir()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function subir(files: FileList | null, ruta: string) {
    const file = files?.[0]
    if (!file) return
    setError(null)
    const tipo = TIPOS.includes(file.type) ? file.type : EXT_A_TIPO[file.name.split('.').pop()?.toLowerCase() ?? '']
    if (!tipo) {
      setError({texto: t('cargaIps.errors.tipoNoAceptado'), delEnlace: false})
      return
    }
    if (file.size > MAX_BYTES) {
      setError({texto: t('cargaIps.errors.archivoMuyGrande'), delEnlace: false})
      return
    }
    setProgress(0)
    try {
      const target = (
        await api.post<UploadTarget>(`${BASE}/upload-target`, {filename: file.name, content_type: tipo}, CON_COOKIE)
      ).data
      await uploadToStorage(target, file, setProgress)
      const resp = await api.post<Enlace>(
        ruta,
        {
          object_key: target.object_key,
          filename: file.name,
          content_type: tipo,
          size_bytes: file.size,
        },
        CON_COOKIE
      )
      setEnlace(resp.data)
      track(resp.data.retenido && !resp.data.recibido ? 'carga_ips_retenida' : 'carga_ips_recibida', {})
    } catch (e: unknown) {
      setError({texto: translateApiError(e), delEnlace: esDelEnlace(e)})
    } finally {
      setProgress(null)
    }
  }

  return (
    <AuthShell>
      <h1 className="font-heading text-2xl tracking-tight">{t('cargaIps.title')}</h1>
      <p className="mt-2 text-sm text-muted-foreground">{t('cargaIps.intro')}</p>

      {abriendo && <Skeleton className="mt-6 h-24 w-full" data-testid="cargaIps-abriendo" />}

      {enlace && (
        <dl className="surface mt-6 space-y-1 rounded-lg px-4 py-3 text-sm" data-testid="cargaIps-datos">
          <div className="flex flex-wrap items-baseline gap-x-2">
            <dt className="text-muted-foreground">{t('cargaIps.radicado')}:</dt>
            <dd className="font-mono font-medium" data-testid="cargaIps-radicado">
              {enlace.radicado}
            </dd>
          </div>
          <div className="flex flex-wrap items-baseline gap-x-2">
            <dt className="text-muted-foreground">{t('cargaIps.empresa')}:</dt>
            <dd className="font-medium" data-testid="cargaIps-empresa">
              {enlace.empresa}
            </dd>
          </div>
          <p className="flex flex-wrap items-center gap-1.5 pt-1 text-xs text-muted-foreground">
            {t('cargaIps.sinNombre')}
            <PorQue label={t('common.porQue')} testId="cargaIps-sin-nombre-porque">
              <span>{t('cargaIps.sinNombrePorque')}</span>
            </PorQue>
          </p>
        </dl>
      )}

      {enlace?.recibido && (
        <div className="mt-6 space-y-2" data-testid="cargaIps-recibido">
          <Tag tone="green">{t('cargaIps.recibido.tag')}</Tag>
          <p className="text-sm">{t('cargaIps.recibido.texto')}</p>
          {enlace.puede_reemplazar && (
            <>
              <p className="text-xs text-muted-foreground">{t('cargaIps.recibido.ventana')}</p>
              <UploadTrigger
                label={t('cargaIps.recibido.reemplazar')}
                progress={progress}
                testId="cargaIps-reemplazar"
                accept={ACEPTA}
                onPick={(files) => void subir(files, `${BASE}/reemplazar`)}
              />
            </>
          )}
        </div>
      )}

      {enlace?.retenido && !enlace.recibido && (
        <p className="notice-amber mt-6 rounded-lg px-3 py-2 text-sm" data-testid="cargaIps-retenido">
          {t(`cargaIps.retenido.${enlace.retenido_motivo ?? 'no_coincide'}`)}
        </p>
      )}

      {enlace && !enlace.recibido && (
        <div className="mt-4 space-y-2" data-testid="cargaIps-subir">
          {/* Visible por T8 (custodia de datos de salud): qué documento se puede adjuntar y qué nunca. */}
          <p className="flex flex-wrap items-center gap-1.5 text-sm">
            <span>{t('cargaIps.subir.ayuda')}</span>
            <HelpIcon label={t('cargaIps.subir.queEntraLabel')} testId="cargaIps-que-entra">
              {t('cargaIps.subir.queEntra')}
            </HelpIcon>
          </p>
          <Hint>{t('cargaIps.subir.formatoHint')}</Hint>
          <UploadTrigger
            label={t('cargaIps.subir.boton')}
            progress={progress}
            testId="cargaIps-archivo"
            accept={ACEPTA}
            onPick={(files) => void subir(files, BASE)}
          />
        </div>
      )}

      {error && (
        <div className="notice-red mt-6 space-y-2 rounded-lg px-3 py-2 text-sm" data-testid="cargaIps-error">
          <p>{error.texto}</p>
          <p className="text-xs">
            {t(error.delEnlace ? 'cargaIps.errors.queHacer' : 'cargaIps.errors.queHacerArchivo')}
          </p>
          {!enlace && token !== '' && (
            <Button size="sm" variant="outline" data-testid="cargaIps-recargar" disabled={abriendo} onClick={abrir}>
              {t('cargaIps.recargar')}
            </Button>
          )}
        </div>
      )}

      <p className="mt-8 text-xs text-muted-foreground">{t('cargaIps.pie')}</p>
    </AuthShell>
  )
}
